import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API } from "../App";
import { useTranslation } from "react-i18next";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { Button } from "../components/ui/button";
import { Check, ArrowRight } from "lucide-react";
import axios from "axios";

export default function PricingPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const planName = (p) => (lang === "el" && p.name_el) || p.name;
  const planDesc = (p) => (lang === "el" && p.description_el) || p.description;
  const planFeatures = (p) =>
    lang === "el" && Array.isArray(p.features_el) && p.features_el.length ? p.features_el : p.features || [];
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [annual, setAnnual] = useState(false);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await axios.get(`${API}/billing/plans`);
        setPlans(res.data);
      } catch (error) { console.error(error); }
      finally { setLoading(false); }
    };
    fetchPlans();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-medium text-foreground mb-4" data-testid="pricing-heading">
              Simple, transparent pricing
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Choose the plan that fits your venue. Upgrade or downgrade anytime.
            </p>
            {/* Billing toggle */}
            <div className="flex items-center justify-center gap-3 mt-8">
              <span className={`text-sm ${!annual ? "text-foreground font-medium" : "text-muted-foreground"}`}>Monthly</span>
              <button
                onClick={() => setAnnual(!annual)}
                className={`relative w-12 h-6 rounded-full transition-colors ${annual ? "bg-primary" : "bg-muted-foreground/30"}`}
                data-testid="pricing-toggle"
              >
                <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${annual ? "translate-x-6" : "translate-x-0.5"}`} />
              </button>
              <span className={`text-sm ${annual ? "text-foreground font-medium" : "text-muted-foreground"}`}>
                Yearly <span className="text-primary text-xs">Save 17%</span>
              </span>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[1, 2, 3, 4].map((i) => <div key={i} className="animate-pulse bg-muted h-96 rounded-xl" />)}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {plans.map((plan, index) => {
                const isPopular = index === 2; // Professional
                return (
                  <div
                    key={plan.plan_id}
                    className={`relative bg-card rounded-xl border p-6 flex flex-col ${
                      isPopular ? "border-primary shadow-lg scale-105" : "border-border"
                    }`}
                    data-testid={`pricing-plan-${plan.slug}`}
                  >
                    {isPopular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-3 py-0.5 rounded-full text-xs font-medium">
                        Most Popular
                      </div>
                    )}
                    <h3 className="text-lg font-medium text-foreground">{planName(plan)}</h3>
                    <p className="text-sm text-muted-foreground mt-1 mb-6">{planDesc(plan)}</p>
                    <div className="mb-6">
                      <span className="text-4xl font-semibold text-foreground">
                        ${annual ? Math.round(plan.price_yearly / 12) : plan.price_monthly}
                      </span>
                      <span className="text-muted-foreground">/mo</span>
                      {annual && plan.price_yearly > 0 && (
                        <p className="text-xs text-muted-foreground mt-1">${plan.price_yearly}/year</p>
                      )}
                    </div>
                    <ul className="space-y-3 mb-8 flex-1">
                      {planFeatures(plan).map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <Check className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                          {f}
                        </li>
                      ))}
                    </ul>
                    <Link to={`/contact?plan=${encodeURIComponent(planName(plan) || plan.slug)}`}>
                      <Button variant={isPopular ? "default" : "outline"} className="w-full" data-testid={`pricing-select-${plan.slug}`}>
                        Contact us <ArrowRight className="h-4 w-4 ml-1" />
                      </Button>
                    </Link>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
